/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useMemo} from 'react';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
// eslint-disable-next-line no-restricted-imports
import {useRouter} from 'next/compat/router';
import {get} from '@ion/core/src/environment/environment';
import useRootManager from '@ion/dls/src/client/useRootManager';
import {dlsSidebarDockedStyle} from '@ion/dls/src/components/navigation/NavigationStore';
import {toggleCollapseStyle} from '@ion/dls/src/components/navigation/useExpandCollapse';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import useThemeContext from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';

const ELD_FRAME_ID = 'eld-iframe';

const useStyles = makeStyles<DLSTheme>((theme) => ({
  container : {
    width                          : toggleCollapseStyle('calc(100vw - 50px)', 'calc(100vw - 105px)'),
    height                         : 'calc(100vh - 45px)',
    position                       : 'absolute',
    top                            : '45px',
    left                           : toggleCollapseStyle('50px', '105px'),
    transition                     : 'width 0.2s ease-in-out, left 0.2s ease-in-out',
    overflow                       : 'hidden',
    [theme.breakpoints.down('sm')] : {
      height : 'calc(100vh - 103px)',
      width  : '100% !important',
      top    : '95px',
      left   : '0px !important'
    }
  },
  // overrides `container` for current theme desktop only - current theme's
  // app chrome has no fixed top header taking up space, so the portal spans
  // the full viewport height, and the side nav's width is a fixed pixel
  // token rather than the legacy nav's 50px/105px, so the portal must dock
  // at a matching left offset with a matching width ...
  currentContainer : {
    ...dlsSidebarDockedStyle,
    height : '100vh !important',
    top    : '0px !important'
  },
  // overrides `container` for current theme mobile only - a separate class
  // rather than a breakpoint nested inside `currentContainer`, since JSS
  // emits that class's dynamic (collapsed-dependent) width/left as a
  // higher-priority dynamic stylesheet that beats a nested static @media
  // rule regardless of source order - the side nav is not a persistent
  // side panel on mobile, so the portal spans the full viewport width ...
  currentMobileContainer : {
    width  : '100vw !important',
    left   : '0px !important',
    height : 'calc(100vh - 99px) !important',
    top    : '56px !important'
  },
  iFrame : {
    width  : '100%',
    height : '100%',
    border : 'none'
  }
}));

const useELDPortal = ({collapsed}) => {
  const classes                     = useStyles({collapsed});
  const {isCurrent: isCurrentTheme} = useThemeContext();
  const {isPhone}                   = useMedia();
  const containerClass              = classnames(classes.container, {
    [classes.currentContainer]       : isCurrentTheme && !isPhone,
    [classes.currentMobileContainer] : isCurrentTheme && isPhone
  });
  const route                       = useRouter();
  const isELDPage                   = route?.route === '/eld';
  const {
    render,
    getContainer,
    createContainer
  } = useRootManager(false);
  let frameContainer = getContainer(ELD_FRAME_ID);

  useEffect(() => {
    if (isELDPage) {
      if (!frameContainer) {
        frameContainer = createContainer(ELD_FRAME_ID);
        document.body.appendChild(frameContainer);

        render(
          <div className={containerClass}>
            <iframe src={get('apolloPortalURL')} className={classes.iFrame} />
          </div>,
          frameContainer
        );
      } else {
        frameContainer.style.display = 'block';
      }
    };
  }, [isELDPage]);

  useEffect(() => {
    return () => {
      frameContainer && (frameContainer.style.display = 'none');
    };
  }, [isELDPage]);
};

export default useELDPortal;
